import type { DimensionHistoryAction, OrgDimensionKind } from '@alinea/types';

/**
 * Copy shared by the departments and cost centers screens: the hierarchy table, the inactivate
 * dialog and the history sheet. Only the nouns change between the two kinds, so the sentences are
 * built here once from `nouns`.
 */
interface Nouns {
  /** "departamento" */
  one: string;
  /** "o departamento" */
  the: string;
  /** "sub-departamento" */
  child: string;
  /** "sub-departamentos" */
  children: string;
  /** "Este departamento" */
  thisOne: string;
  /** "departamento pai" */
  parent: string;
}

const NOUNS: Record<OrgDimensionKind, Nouns> = {
  department: {
    one: 'departamento',
    the: 'o departamento',
    child: 'sub-departamento',
    children: 'sub-departamentos',
    thisOne: 'Este departamento',
    parent: 'departamento pai',
  },
  cost_center: {
    one: 'centro de custo',
    the: 'o centro de custo',
    child: 'sub-centro',
    children: 'sub-centros',
    thisOne: 'Este centro de custo',
    parent: 'centro de custo pai',
  },
};

export function orgDimensionCopy(kind: OrgDimensionKind) {
  const n = NOUNS[kind];
  return {
    table: {
      headers: { name: 'Nome', status: 'Status', actions: 'Ações' },
      status: { active: 'Ativo', inactive: 'Inativo' },
      aria: { expand: 'Expandir', collapse: 'Recolher' },
      empty: `Nenhum ${n.one} para mostrar. Troque o filtro de status ou crie um novo.`,
      noResults: `Nenhum ${n.one} encontrado.`,
      subCount: (count: number) => `${count} ${count === 1 ? n.child : n.children}`,
      actions: {
        addChild: `Adicionar ${n.child}`,
        details: 'Ver detalhes',
        history: 'Ver histórico',
        inactivate: 'Inativar',
        reactivate: 'Reativar',
      },
      tooltips: {
        addChild: `Adicionar ${n.child}`,
        details: 'Ver detalhes',
        history: 'Ver histórico',
        inactivate: `Inativar ${n.one}`,
        reactivate: `Reativar ${n.one}`,
        reactivateBlocked: `Reative o ${n.parent} primeiro`,
      },
    },
    filters: {
      status: {
        label: 'Status',
        active: 'Ativos',
        inactive: 'Inativos',
        all: 'Todos',
      },
    },
    inactivate: {
      title: (name: string) => `Inativar “${name}”?`,
      description: `${n.thisOne} deixa de aparecer nas listas para novos lançamentos.`,
      impact: (count: number) =>
        count === 0
          ? `Não há ${n.children} ativos abaixo.`
          : `Também serão inativados ${count} ${count === 1 ? n.child : n.children}.`,
      kept: 'Lançamentos que já usam este item continuam como estão. Você pode reativar depois.',
      reasonLabel: 'Motivo (opcional)',
      reasonPlaceholder: 'Ex.: área encerrada em outubro',
      confirm: 'Inativar',
      cancel: 'Cancelar',
      failed: `Não foi possível inativar ${n.the}. Tente de novo.`,
    },
    reactivate: {
      failed: `Não foi possível reativar ${n.the}. Tente de novo.`,
    },
    history: {
      title: (name: string) => `Histórico de “${name}”`,
      description: 'Quem mudou, quando, e o valor antes e depois.',
      loading: 'Carregando histórico...',
      empty: 'Ainda não há mudanças registradas.',
      failed: 'Não foi possível carregar o histórico.',
      by: (actor: string) => `por ${actor}`,
      system: 'Sincronização automática',
      unknownActor: 'Usuário removido',
      reason: (reason: string) => `Motivo: ${reason}`,
      cascadedFrom: (name: string) => `Junto com “${name}”`,
      counterpart: (name: string) => `Com “${name}”`,
      before: 'Antes',
      after: 'Depois',
      emptyValue: 'vazio',
      changed: 'Alterado',
      yes: 'Sim',
      no: 'Não',
      close: 'Fechar',
    },
  };
}

export const orgDimensionHistoryActions: Record<DimensionHistoryAction, string> = {
  create: 'Criado',
  rename: 'Renomeado',
  update: 'Alterado',
  inactivate: 'Inativado',
  reactivate: 'Reativado',
  delete: 'Excluído',
  link: 'Vínculo adicionado',
  unlink: 'Vínculo removido',
};

export const orgDimensionFieldLabels: Record<string, string> = {
  name: 'Nome',
  code: 'Código',
  description: 'Descrição',
  aiContext: 'Contexto para IA',
  parentDepartmentId: 'Departamento pai',
  parentCostCenterId: 'Centro de custo pai',
  headUserId: 'Responsável',
  ownerUserId: 'Responsável',
  isMECE: 'Regra MECE',
  requiresMigrationIfInactivated: 'Exige migração ao inativar',
  status: 'Status',
};

export type OrgDimensionCopy = ReturnType<typeof orgDimensionCopy>;
